import { useEffect, useState } from "react";
import { memberManagerCoachStats, memberManagerReports, exitReasonsRollup } from "@/lib/api";
import { UserCog, ChevronUp, ChevronDown, MessagesSquare, DoorOpen, AlertTriangle } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

const REASON_COLORS = ["#f43f5e", "#f59e0b", "#60a5fa", "#a78bfa", "#34d399", "#94a3b8"];

// Tiny helpers that replace nested ternaries throughout this page. Keeping them at module
// scope lets the code review linter stop flagging the file while still expressing the
// same "green ≥ 80, amber ≥ 50, red otherwise" rule in one obvious place.
const followUpTintClass = (pct) => {
  if (pct >= 80) return "text-emerald-300";
  if (pct >= 50) return "text-amber-300";
  return "text-rose-300";
};
const followUpTintKey = (pct) => {
  if (pct >= 80) return "emerald";
  if (pct >= 50) return "amber";
  return "rose";
};

const KPI_TINT = { emerald: "text-emerald-300", amber: "text-amber-300", rose: "text-rose-300", blue: "text-blue-300" };
const REPORT_BORDER = { amber: "border-amber-500/30 bg-amber-500/[0.03]", rose: "border-rose-500/30 bg-rose-500/[0.03]" };
const REPORT_ICON = { amber: "text-amber-400", rose: "text-rose-400", emerald: "text-emerald-400" };

/**
 * Retention · a top-level surface hosting the Member Manager dashboard for now.
 * More retention tools will land here later (renewal funnels, exit-reason analytics, etc.).
 */
export default function Retention() {
  const [stats, setStats] = useState([]);
  const [reports, setReports] = useState({ stale_updates: [], expired_no_reason: [] });
  const [showTable, setShowTable] = useState(true);
  const [exitRollup, setExitRollup] = useState({ months: [], top: [] });
  useEffect(() => {
    memberManagerCoachStats().then(setStats).catch(() => setStats([]));
    memberManagerReports().then(setReports).catch(() => {});
    exitReasonsRollup(6).then(setExitRollup).catch(() => {});
  }, []);

  const totalAssigned = stats.reduce((a, s) => a + (s.total_assigned || 0), 0);
  const totalActive = stats.reduce((a, s) => a + (s.active || 0), 0);
  const totalExited = stats.reduce((a, s) => a + (s.exited || 0), 0);
  const totalFresh = stats.reduce((a, s) => a + (s.fresh_updates || 0), 0);
  const totalUpForRenewal = stats.reduce((a, s) => a + (s.up_for_renewal || 0), 0);
  const overallFollowUp = totalActive > 0 ? Math.round((100 * totalFresh) / Math.max(1, totalActive - stats.reduce((a, s) => a + (s.in_fe || 0), 0))) : 0;

  return (
    <div className="space-y-8" data-testid="retention-page">
      <div>
        <div className="eyebrow">Retention · post-Front-End</div>
        <h2 className="font-display text-3xl font-extrabold mt-1 tracking-tight">Retention</h2>
        <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
          How well are Member Managers keeping members engaged after their initial Front End programme?
          Track follow-up cadence, renewal risk, and exit patterns across the whole coaching team.
        </p>
      </div>

      {/* Roll-up KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="retention-kpis">
        <Kpi label="Members assigned" value={totalAssigned} />
        <Kpi label="Active" value={totalActive} tint="emerald" />
        <Kpi label="Up for renewal" value={totalUpForRenewal} tint="amber" />
        <Kpi label="Exited" value={totalExited} tint="rose" />
        <Kpi label="Overall follow-up" value={`${overallFollowUp}%`} tint={followUpTintKey(overallFollowUp)} />
      </div>

      {/* Coach leaderboard (podium) */}
      <section data-testid="retention-leaderboard">
        <div className="flex items-center gap-2 mb-3">
          <UserCog className="h-3.5 w-3.5 text-blue-400" />
          <div className="eyebrow">Member Manager leaderboard</div>
          <span className="text-[10px] text-zinc-500">{stats.length} coach{stats.length !== 1 ? "es" : ""}</span>
        </div>
        {stats.length === 0 ? (
          <div className="card-surface p-8 text-center text-sm text-zinc-500">
            No Member Managers assigned to any customer yet. Assign one on the New Sale form (mandatory) or from a customer&apos;s detail view.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {stats.slice(0, 6).map((s, i) => (
              <div key={s.coach} className="card-surface p-4" data-testid={`retention-coach-card-${s.coach}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Rank #{i + 1}</div>
                    <div className="font-display text-lg font-bold text-zinc-100 mt-0.5">{s.coach}</div>
                  </div>
                  <div className="text-right">
                    <div className={`text-2xl font-bold tabular-nums ${followUpTintClass(s.follow_up_rate_pct)}`}>{s.follow_up_rate_pct}%</div>
                    <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-500">follow-up</div>
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-3 gap-2 text-[11px]">
                  <MiniStat label="Assigned" value={s.total_assigned} />
                  <MiniStat label="Active" value={s.active} tint="emerald" />
                  <MiniStat label="In FE" value={s.in_fe} tint="blue" />
                  <MiniStat label="Renewal" value={s.up_for_renewal} tint="amber" />
                  <MiniStat label="Exited" value={s.exited} tint="rose" />
                  <MiniStat label="Audits" value={s.audits_recorded} />
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Full coach table */}
      {stats.length > 0 && (
        <section data-testid="retention-full-table">
          <button data-testid="retention-button-110" onClick={() => setShowTable((v) => !v)} className="w-full flex items-center justify-between mb-3">
            <div className="eyebrow">Full coach breakdown</div>
            {showTable ? <ChevronUp className="h-4 w-4 text-zinc-500" /> : <ChevronDown className="h-4 w-4 text-zinc-500" />}
          </button>
          {showTable && (
            <div className="card-surface overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-zinc-500 bg-[#0d0d0d] border-b border-[#1f1f1f]">
                      <th className="px-3 py-2 font-medium">Coach</th>
                      <th className="px-3 py-2 font-medium text-right">Assigned</th>
                      <th className="px-3 py-2 font-medium text-right">Active</th>
                      <th className="px-3 py-2 font-medium text-right">Expired</th>
                      <th className="px-3 py-2 font-medium text-right">Exited</th>
                      <th className="px-3 py-2 font-medium text-right">In FE</th>
                      <th className="px-3 py-2 font-medium text-right">Renewal</th>
                      <th className="px-3 py-2 font-medium text-right">Fresh &lt;14d</th>
                      <th className="px-3 py-2 font-medium text-right">Stale</th>
                      <th className="px-3 py-2 font-medium text-right">No updates</th>
                      <th className="px-3 py-2 font-medium text-right">Audits</th>
                      <th className="px-3 py-2 font-medium text-right">Follow-up %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.map((s, i) => (
                      <tr key={s.coach} className="border-b border-[#1a1a1a] last:border-0 hover:bg-[#161616]" data-testid={`retention-row-${i}`}>
                        <td className="px-3 py-2 text-zinc-100 font-medium">{s.coach}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{s.total_assigned}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-emerald-300">{s.active}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-zinc-400">{s.expired}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-rose-300">{s.exited}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-blue-300">{s.in_fe}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-amber-300">{s.up_for_renewal}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-emerald-300">{s.fresh_updates}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-amber-300">{s.stale_updates}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-rose-300">{s.no_updates}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-zinc-400">{s.audits_recorded}</td>
                        <td className={`px-3 py-2 text-right tabular-nums font-medium ${followUpTintClass(s.follow_up_rate_pct)}`}>{s.follow_up_rate_pct}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>
      )}

      {/* Exit reason rollup — top 5 per month */}
      <ExitReasonRollup data={exitRollup} />

      {/* Reports strip */}
      <section data-testid="retention-reports">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <ReportBlock
            icon={MessagesSquare}
            tint="amber"
            label="Stale members"
            count={reports.stale_updates.length}
            description="Active non-FE members with no Member Manager update in the last 14 days."
            items={reports.stale_updates.slice(0, 6).map((r) => ({ key: r.customer_key, primary: r.name, secondary: `${r.member_manager || "—"} · ${r.days_since_last_update ?? "no updates"}` }))}
            testid="report-stale-members"
          />
          <ReportBlock
            icon={DoorOpen}
            tint="rose"
            label="Expired · no reason"
            count={reports.expired_no_reason.length}
            description="Programmes ended without a recorded non-renewal reason. Fill these to unlock exit-reason analytics."
            items={reports.expired_no_reason.slice(0, 6).map((r) => ({ key: r.customer_key, primary: r.name, secondary: `${r.member_manager || "—"} · ${r.days_since_end}d overdue` }))}
            testid="report-expired-no-reason"
          />
        </div>
      </section>
    </div>
  );
}

function Kpi({ label, value, tint }) {
  const cls = KPI_TINT[tint] || "text-zinc-100";
  return (
    <div className="card-surface p-4">
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</div>
      <div className={`font-display text-3xl font-extrabold mt-1 tabular-nums ${cls}`}>{value}</div>
    </div>
  );
}

function MiniStat({ label, value, tint }) {
  const cls = KPI_TINT[tint] || "text-zinc-200";
  return (
    <div className="rounded-md border border-[#1f1f1f] bg-[#0d0d0d] px-2 py-1.5">
      <div className="text-[9px] uppercase tracking-[0.18em] text-zinc-500">{label}</div>
      <div className={`font-medium tabular-nums ${cls}`}>{value}</div>
    </div>
  );
}

function ReportBlock({ icon: Icon, tint, label, count, description, items, testid }) {
  const tintCls = REPORT_BORDER[tint] || "border-[#262626]";
  const iconCls = REPORT_ICON[tint] || "text-zinc-400";
  return (
    <div className={`card-surface p-4 border ${tintCls}`} data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Icon className={`h-4 w-4 ${iconCls}`} />
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">{label}</div>
          </div>
          <div className="mt-2 text-3xl font-bold text-zinc-100 tabular-nums">{count}</div>
          <div className="text-[11px] text-zinc-500 mt-0.5">{description}</div>
        </div>
      </div>
      {count > 0 && (
        <ul className="mt-3 space-y-1">
          {(items || []).map((it) => (
            <li key={it.key} className="text-[11px] text-zinc-400"><span className="text-zinc-200">{it.primary}</span> <span className="text-zinc-500">· {it.secondary}</span></li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ExitReasonRollup({ data }) {
  const months = data.months || [];
  const top = data.top || [];
  const totalExits = months.reduce((a, m) => a + (m.total || 0), 0);
  // Recharts-friendly data: one row per month; keys = top reasons + Other bucket
  const chartData = months.map((m) => {
    const row = { label: m.label, total: m.total || 0 };
    let otherSum = 0;
    for (const [reason, n] of Object.entries(m.reasons || {})) {
      if (top.includes(reason)) row[reason] = n;
      else otherSum += n;
    }
    if (otherSum > 0) row["Other"] = otherSum;
    return row;
  });
  const allKeys = [...top];
  if ((chartData || []).some((r) => r["Other"] != null)) allKeys.push("Other");

  return (
    <section data-testid="retention-exit-rollup">
      <div className="flex items-baseline justify-between mb-3">
        <div className="flex items-center gap-2">
          <DoorOpen className="h-4 w-4 text-rose-400" />
          <div>
            <div className="eyebrow">Top exit reasons · last 6 months</div>
            <div className="text-xs text-zinc-500 mt-0.5">Spot rising patterns so you can address them before they compound.</div>
          </div>
        </div>
        <div className="text-[11px] text-zinc-500 tabular-nums">{totalExits} total exit{totalExits === 1 ? "" : "s"}</div>
      </div>
      <div className="card-surface p-6">
        {totalExits === 0 ? (
          <div className="text-sm text-zinc-500 py-8 text-center">No exits recorded in this window yet.</div>
        ) : (
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 8, right: 12, left: -20, bottom: 0 }}>
                <CartesianGrid stroke="#1f1f1f" vertical={false} />
                <XAxis dataKey="label" stroke="#52525b" fontSize={11} tickLine={false} axisLine={{ stroke: "#1f1f1f" }} />
                <YAxis stroke="#52525b" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#0f0f0f", border: "1px solid #262626", borderRadius: 8, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {allKeys.map((k, i) => (
                  <Bar key={k} dataKey={k} stackId="a" fill={REASON_COLORS[i % REASON_COLORS.length]} radius={i === allKeys.length - 1 ? [4, 4, 0, 0] : 0} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </section>
  );
}
